import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { journeyAPI } from '../../services/api'
import BatchEvaluationPanel from './BatchEvaluationPanel'
import BatchEvaluationLLMPanel from './BatchEvaluationLLMPanel'

export default function JourneyList() {
  const [journeys, setJourneys] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const navigate = useNavigate()

  useEffect(() => {
    loadJourneys()
  }, [])

  const loadJourneys = async () => {
    setLoading(true)
    try {
      const data = await journeyAPI.list()
      setJourneys(data)
    } catch (err) {
      setError('Failed to load journeys')
    } finally {
      setLoading(false)
    }
  }

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const completedJourneys = journeys.filter(j => j.status === 'completed')

  const toggleSelectAll = () => {
    if (selectedIds.size === completedJourneys.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(completedJourneys.map(j => j.id)))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
      </div>
    )
  }

  if (journeys.length === 0) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body items-center text-center py-12">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-semibold">No journeys yet</h3>
          <p className="text-base-content/70">
            Generate a journey from the "Generate Journey" tab to see it here.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Generated Journeys</h2>
        {completedJourneys.length > 0 && (
          <label className="label cursor-pointer gap-2">
            <span className="label-text text-sm">Select all completed</span>
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={selectedIds.size === completedJourneys.length && completedJourneys.length > 0}
              onChange={toggleSelectAll}
            />
          </label>
        )}
      </div>

      {journeys.map(journey => {
        const isCompleted = journey.status === 'completed'
        const isSelected = selectedIds.has(journey.id)

        return (
          <div key={journey.id} className={`card bg-base-100 shadow hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-primary' : ''}`}>
            <div className="card-body p-4 flex-row items-center justify-between">
              {isCompleted && (
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm mr-3"
                  checked={isSelected}
                  onChange={() => toggleSelection(journey.id)}
                />
              )}
              <div className="flex-1">
                <h3 className="font-semibold">{journey.persona_name}</h3>
                <div className="flex gap-3 text-sm text-base-content/60 mt-1">
                  <span>Patient: {journey.patient_name}</span>
                  <span>{journey.total_episodes} episodes</span>
                  <span className={`badge badge-sm ${journey.status === 'completed' ? 'badge-success' : journey.status === 'failed' ? 'badge-error' : 'badge-warning'}`}>
                    {journey.status}
                  </span>
                </div>
                <div className="text-xs text-base-content/40 mt-1">
                  {new Date(journey.created_at).toLocaleDateString()} {new Date(journey.created_at).toLocaleTimeString()}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => journeyAPI.exportJson(journey.id)}
                >
                  Export
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(`/generator/journey/${journey.id}`)}
                  disabled={!isCompleted}
                >
                  View
                </button>
              </div>
            </div>
          </div>
        )
      })}

      {/* Batch Triage Evaluation */}
      <BatchEvaluationPanel selectedJourneyIds={Array.from(selectedIds)} />

      {/* LLM-Judge Batch Evaluation */}
      <BatchEvaluationLLMPanel selectedJourneyIds={Array.from(selectedIds)} />
    </div>
  )
}
